
import React from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/shared/ui/ErrorBoundary";
import { ErrorState } from "@/shared/ui/states/ErrorState";
import { LoadingState } from "@/shared/ui/states/LoadingState";
import { GeoSessionProvider } from "@/shared/providers/GeoSessionProvider";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { AuthWrapper } from "@/app/AuthWrapper";
import { Layout } from "@/shared/ui/Layout";
import { PERMISSIONS } from "@/shared/constants/permissions";
import { usePageTitle } from "@/shared/hooks/usePageTitle";
import { useRuntimeConfig } from "@/shared/hooks/useRuntimeConfig";
import { adminRoutes } from "./routes/adminRoutes";
import { operationsRoutes } from "./routes/operationsRoutes";
import { systemRoutes } from "./routes/systemRoutes";
import { toolsRoutes } from "./routes/toolsRoutes";
import { AppRoutes } from "@/app/routes";

// Lazy load top-level pages
const Fleet = React.lazy(() => import("@/features/fleet/components/Fleet"));
const DispatcherMfeDashboard = React.lazy(
  () => import("@/features/dashboardMfe/components/DispatcherMfeDashboard"),
);
const Login = React.lazy(() => import("@/shared/components/Login"));
const ForgotPassword = React.lazy(() => import("@/shared/components/ForgotPassword"));
const LostPassword = React.lazy(() => import("@/shared/components/LostPassword"));
const SSOCallback = React.lazy(() => import('@/shared/components/SSOCallback'));
const RootRedirect = React.lazy(() => import('@/shared/components/RootRedirect'));
const NoAccessPage = React.lazy(() => import("@/pages/NoAccessPage"));
const DashboardNoAccessPage = React.lazy(() => import("@/pages/DashboardNoAccessPage"));
const NotFoundPage = React.lazy(() => import("@/pages/NotFoundPage"));

// ---------------------------------------------------------------------------
// ChunkErrorBoundary
// Wraps every React.Suspense boundary so that a "Failed to fetch dynamically
// imported module" error (triggered by a new deployment replacing old chunk
// hashes) is caught close to the lazy component, auto-reloads once, and — if
// the reload already happened — shows a friendly "Refresh Now" prompt instead
// of crashing the whole page.
// ---------------------------------------------------------------------------
const GlassRouteFallback: React.FC = () => (
  <div
    className="absolute inset-0 z-10 pointer-events-none bg-surface/60"
    aria-label="Loading"
    role="status"
    data-testid="route-glass-loader"
  />
);

const ChunkErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ErrorBoundary fallback={ErrorState}>{children}</ErrorBoundary>
);

// Layout wrapper component with authentication and shared header
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ECOPD-17017 — read the tab-title system name here and pass it down so
  // usePageTitle stays free of the runtime-config dependency.
  const { getConfigValue } = useRuntimeConfig();
  usePageTitle(getConfigValue<string>("general.system.name"));
  return (
    <ErrorBoundary fallback={ErrorState}>
      <AuthWrapper>
        {/*
         * GeoSessionProvider mints a geo-session token (POST /drt/api/bff/geo-session)
         * once per authenticated session and keeps it fresh. The token is stored in
         * geoTokenStore and attached as X-Geo-Token by the apiClient request interceptor
         * on all /api/v1/* (maps proxy) calls.
         */}
        <GeoSessionProvider>
          <Layout>
            <ChunkErrorBoundary>
              <React.Suspense fallback={<GlassRouteFallback />}>
                {children}
              </React.Suspense>
            </ChunkErrorBoundary>
          </Layout>
        </GeoSessionProvider>
      </AuthWrapper>
    </ErrorBoundary>
  );
};

export const router = createBrowserRouter([
  // --- Auth / standalone pages (no layout) ---
  {
    path: "/login",
    element: (
      <ChunkErrorBoundary>
        <React.Suspense fallback={<LoadingState message="Loading..." />}>
          <Login />
        </React.Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <ChunkErrorBoundary>
        <React.Suspense fallback={<LoadingState message="Loading..." />}>
          <ForgotPassword />
        </React.Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/lost-password/",
    element: (
      <ChunkErrorBoundary>
        <React.Suspense fallback={<LoadingState message="Loading..." />}>
          <LostPassword />
        </React.Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/callback",
    element: (
      <ChunkErrorBoundary>
        <React.Suspense fallback={<LoadingState message="Loading..." />}>
          <SSOCallback />
        </React.Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/",
    element: (
      <ChunkErrorBoundary>
        <React.Suspense fallback={<LoadingState message="Loading..." />}>
          <RootRedirect />
        </React.Suspense>
      </ChunkErrorBoundary>
    ),
  },

  // --- Top-level app pages ---
  {
    path: "/no-access",
    element: (
      <AppLayout>
        <NoAccessPage />
      </AppLayout>
    ),
  },
  {
    // ECOPD-18453: /dashboard used to render an early placeholder page with
    // hard-coded sample data (TRP-001, VH-003, "System Status: Online"). Users
    // have it bookmarked, so it now goes to the real dashboard. Bare redirect:
    // the target route carries the permission gate, so nothing is bypassed here.
    path: AppRoutes.dashboard,
    element: <Navigate to={AppRoutes.dashboardMfe} replace />,
  },
  {
    // /home → the Fleet dashboard too. Same bare redirect: the Fleet route checks
    // dashboard_view and shows the dashboard no-access page without it.
    path: AppRoutes.home,
    element: <Navigate to={AppRoutes.dashboardMfe} replace />,
  },
  {
    path: "/fleet",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.VEHICLE_VIEW}>
          <Fleet />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    // Federated Dispatcher MFE — the 6.2 Fleet Dashboard consumed as a remote
    // instead of bundled into this app. The trailing splat is REQUIRED: the remote
    // renders its own descendant <Routes> beneath this path.
    //
    // Ranking: "/dashboard/fleet/*" has a static segment more than the legacy
    // "/dashboard" above (a leaf, it never matches deeper URLs), so it wins for
    // every URL under it regardless of array order.
    path: `${AppRoutes.dashboardMfe}/*`,
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.DASHBOARD_VIEW}
          fallback={
            <React.Suspense fallback={null}>
              <DashboardNoAccessPage />
            </React.Suspense>
          }
        >
          <DispatcherMfeDashboard />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    // Old mount path (dev/qa3 bookmarks, Pendo guides). Bare redirect: the target
    // route carries the permission gate, so nothing is bypassed here.
    path: `${AppRoutes.dashboardMfeLegacy}/*`,
    element: <Navigate to={AppRoutes.dashboardMfe} replace />,
  },
  {
    path: "/riders",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.CUSTOMER_VIEW}
        >
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Riders</h1>
            <p className="text-text-muted">Coming Soon</p>
          </div>
        </ProtectedRoute>
      </AppLayout>
    ),
  },

  // --- Feature route groups ---
  ...adminRoutes(AppLayout),
  ...operationsRoutes(AppLayout),
  ...systemRoutes(AppLayout),
  ...toolsRoutes(AppLayout),

  // --- Catch-all ---
  {
    path: "*",
    element: (
      <ChunkErrorBoundary>
        <React.Suspense fallback={<LoadingState message="Loading..." />}>
          <NotFoundPage />
        </React.Suspense>
      </ChunkErrorBoundary>
    ),
  },
]);
